/**
 * *New layer* — `docs/property-maps-v2.md` §5.6, board 5.
 *
 * Name, colour, who sees it, and the pin. **The first unused colour is
 * preselected**, so three layers made in a row are three different colours
 * without anybody choosing — which is what makes the panel readable at a
 * glance, and what nobody does by hand.
 *
 * **It starts as All crews** (§12 rule 4), like every new layer, and the
 * sharing control here is the same one the sharing dialog draws so the two
 * cannot describe the decision differently.
 */
import { useState } from 'react'
import { LAYER_COLORS, LAYER_PALETTE } from '@rowted/shared'
import type { Audience, LayerColor } from '@rowted/shared'
import { Button, Switch } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { Field, TextInput } from '../../components/form/index.js'
import { cn } from '../../lib/cn.js'
import { AudienceField, audienceReady } from '../sharing/audience-field.js'
import type { Division } from '../sharing/audience-chip.js'

export function NewLayerDialog({
  mapName, divisions, taken, onCancel, onCreate,
}: {
  mapName: string
  divisions: Division[]
  /** The colours already in use on this map. */
  taken: LayerColor[]
  onCancel: () => void
  onCreate: (body: {
    name: string; color: LayerColor; audience: Audience
    divisionIds: string[]; pinnedToTickets: boolean
  }) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState<LayerColor>(
    LAYER_COLORS.find((one) => !taken.includes(one)) ?? LAYER_COLORS[0])
  const [audience, setAudience] = useState<Audience>('all')
  const [divisionIds, setDivisionIds] = useState<string[]>([])
  const [pinned, setPinned] = useState(false)
  const [busy, setBusy] = useState(false)

  const ready = name.trim().length > 0 && audienceReady(audience, divisionIds) && !busy

  return (
    <SheetOrDialog
      onClose={onCancel}
      title="New layer"
      sub={`On “${mapName}”`}
      width={520}
      footer={(
        <>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() => {
              setBusy(true)
              void onCreate({
                name: name.trim(), color, audience, divisionIds, pinnedToTickets: pinned,
              }).finally(() => setBusy(false))
            }}
          >
            {busy ? 'Creating…' : 'Create layer'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4 px-5 py-4">
        <Field label="Layer name" required>
          {({ id }) => (
            <TextInput
              id={id}
              value={name}
              autoFocus
              placeholder="Plow zones, Hazards, Irrigation heads"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Colour">
          {() => (
            <div className="flex items-center gap-2">
              {LAYER_COLORS.map((one) => (
                <button
                  key={one}
                  type="button"
                  aria-label={one}
                  aria-pressed={one === color}
                  onClick={() => setColor(one)}
                  className={cn('h-6 w-6 rounded-tile')}
                  style={{
                    background: LAYER_PALETTE[one],
                    ...(one === color
                      ? {
                        boxShadow: '0 0 0 2px rgb(var(--color-surface)),'
                          + ` 0 0 0 4px ${LAYER_PALETTE[one]}`,
                      }
                      : {}),
                  }}
                />
              ))}
            </div>
          )}
        </Field>

        <Field label="Who sees it">
          {() => (
            <AudienceField
              audience={audience}
              divisionIds={divisionIds}
              divisions={divisions}
              onChange={(next) => { setAudience(next.audience); setDivisionIds(next.divisionIds) }}
            />
          )}
        </Field>

        <Switch checked={pinned} onChange={setPinned} label="Pin to visits here" />

        <p className="m-0 text-cell text-text-muted">Not shared with customers.</p>
      </div>
    </SheetOrDialog>
  )
}
